import React, { useState, useEffect } from "react";
import api from "../utils/api";
import {
    Users,
    Plus,
    Search,
    Edit2,
    Trash2,
    CheckCircle,
    XCircle,
    Key,
    Globe,
    RefreshCw,
    Copy
} from "lucide-react";
import StatCard from "./inventory/StatCard";
import Modal from "../components/Modal";

export default function ApiPartners() {
    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [editingPartner, setEditingPartner] = useState(null);

    // Form State
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        status: "active",
        role: "read-only",
    });

    // Base URL - assumes backend is proxy'd or on same host 
    // (React setup typically proxies /api to backend)
    const API_URL = "/api/partners";

    useEffect(() => {
        fetchPartners();
    }, []);

    const fetchPartners = async () => {
        setLoading(true);
        try {
            const res = await api.get(API_URL);
            setPartners(res.data);
        } catch (err) {
            console.error("Failed to fetch partners", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingPartner) {
                await api.put(`${API_URL}/${editingPartner.id}`, formData);
            } else {
                await api.post(API_URL, formData);
            }
            setShowModal(false);
            setEditingPartner(null);
            setFormData({ name: "", email: "", status: "active", role: "read-only" });
            fetchPartners();
        } catch (err) {
            console.error("Failed to save partner", err);
            alert("Error saving partner");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this partner?")) return;
        try {
            await api.delete(`${API_URL}/${id}`);
            fetchPartners();
        } catch (err) {
            console.error("Failed to delete partner", err);
        }
    };

    const openEdit = (partner) => {
        setEditingPartner(partner);
        setFormData({
            name: partner.name,
            email: partner.email,
            status: partner.status,
            role: partner.role
        });
        setShowModal(true);
    };

    const openAdd = () => {
        setEditingPartner(null);
        setFormData({ name: "", email: "", status: "active", role: "read-only" });
        setShowModal(true);
    };

    const filteredPartners = partners.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="max-w-7xl mx-auto pb-12">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-navy-900 font-serif">API Partners</h1>
                    <p className="text-slate-500 mt-1">Manage external integrations and API access keys.</p>
                </div>
                <button
                    onClick={openAdd}
                    className="px-4 py-2 bg-navy-900 text-white rounded-lg hover:bg-navy-800 font-medium flex items-center gap-2 transition-colors shadow-lg shadow-navy-900/20"
                >
                    <Plus size={18} /> Add Partner
                </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <StatCard
                    label="Total Partners"
                    value={partners.length}
                    icon={Users}
                    colorClass="bg-blue-500"
                />
                <StatCard
                    label="Active Connections"
                    value={partners.filter(p => p.status === 'active').length}
                    icon={CheckCircle}
                    colorClass="bg-emerald-500"
                />
                <StatCard
                    label="Inactive"
                    value={partners.filter(p => p.status !== 'active').length}
                    icon={XCircle}
                    colorClass="bg-red-500"
                />
            </div>

            {/* Toolbar */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 mb-6 flex items-center justify-between">
                <div className="relative w-full md:w-96">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                        type="text"
                        className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-900 transition-all placeholder:text-slate-400 text-sm"
                        placeholder="Search partners..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <button
                    onClick={fetchPartners}
                    className="p-2 text-slate-400 hover:text-navy-900 hover:bg-slate-50 rounded-lg transition-colors"
                    title="Refresh List"
                >
                    <RefreshCw size={20} />
                </button>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-slate-500">Loading partners...</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-left">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="px-6 py-4 text-xs font-bold text-navy-900 uppercase tracking-wider">Partner Name</th>
                                    <th className="px-6 py-4 text-xs font-bold text-navy-900 uppercase tracking-wider">Contact Email</th>
                                    <th className="px-6 py-4 text-xs font-bold text-navy-900 uppercase tracking-wider">API Key</th>
                                    <th className="px-6 py-4 text-xs font-bold text-navy-900 uppercase tracking-wider">Role</th>
                                    <th className="px-6 py-4 text-xs font-bold text-navy-900 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-4 text-xs font-bold text-navy-900 uppercase tracking-wider text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {filteredPartners.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-8 text-center text-slate-400">
                                            No partners found.
                                        </td>
                                    </tr>
                                ) : filteredPartners.map((partner) => (
                                    <tr key={partner.id} className="hover:bg-slate-50 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-navy-50 flex items-center justify-center text-navy-700 font-bold border border-navy-100 text-xs">
                                                    {(partner.name || "??").substring(0, 2).toUpperCase()}
                                                </div>
                                                <span className="font-medium text-navy-900">{partner.name || "Unknown Partner"}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-slate-600">
                                            {partner.email || "-"}
                                        </td>
                                        <td className="px-6 py-4">
                                            <code className="bg-slate-100 px-2 py-1 rounded text-xs font-mono text-slate-600 flex items-center gap-2 w-fit">
                                                <Key size={10} />
                                                {partner.apiKey ? `${partner.apiKey.substring(0, 8)}...` : 'No Key'}
                                                <Copy size={10} className="cursor-pointer hover:text-navy-900" onClick={() => navigator.clipboard.writeText(partner.apiKey || "")} />
                                            </code>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 capitalize">
                                                {partner.role || "read-only"}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full flex items-center gap-1 w-fit ${partner.status === 'active'
                                                ? 'bg-emerald-100 text-emerald-800'
                                                : 'bg-slate-100 text-slate-600'
                                                }`}>
                                                {partner.status === 'active' ? <CheckCircle size={10} /> : <XCircle size={10} />}
                                                {(partner.status || "inactive").toUpperCase()}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={() => openEdit(partner)}
                                                    className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                >
                                                    <Edit2 size={16} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(partner.id)}
                                                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal */}
            <Modal open={showModal} onClose={() => setShowModal(false)}>
                <div className="p-6">
                    <h2 className="text-xl font-bold text-navy-900 mb-6 font-serif">
                        {editingPartner ? 'Edit Partner' : 'Add New Partner'}
                    </h2>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-semibold text-navy-900 mb-1.5">Partner Name</label>
                            <input
                                type="text"
                                required
                                className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-900"
                                value={formData.name}
                                onChange={e => setFormData({ ...formData, name: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-navy-900 mb-1.5">Contact Email</label>
                            <input
                                type="email"
                                required
                                className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-900"
                                value={formData.email}
                                onChange={e => setFormData({ ...formData, email: e.target.value })}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold text-navy-900 mb-1.5">Role</label>
                                <select
                                    className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-900"
                                    value={formData.role}
                                    onChange={e => setFormData({ ...formData, role: e.target.value })}
                                >
                                    <option value="read-only">Read Only</option>
                                    <option value="admin">Admin</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-navy-900 mb-1.5">Status</label>
                                <select
                                    className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-900"
                                    value={formData.status}
                                    onChange={e => setFormData({ ...formData, status: e.target.value })}
                                >
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-8">
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                className="px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg font-medium transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-6 py-2 bg-navy-900 text-white rounded-lg hover:bg-navy-800 font-medium transition-colors shadow-lg shadow-navy-900/20"
                            >
                                {editingPartner ? 'Save Changes' : 'Create Partner'}
                            </button>
                        </div>
                    </form>
                </div>
            </Modal>
        </div>
    );
}
